'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormatter, useLocale, useTranslations } from 'next-intl';
import { AlertCircle, Check, Heart, Lock, Package, Truck } from 'lucide-react';

import { trpc, trpcVanilla } from '@/lib/trpc';
import { Input } from '@/components/ui/input/input';

import { usePaymentConfig } from '../../hooks/usePaymentConfig';
import { useRegistrationStore } from '../../stores/registration-store';
import { trackRego } from '../../lib/registration-analytics';
import {
  getCheckoutItemsSubtotal,
  getSelectedCheckoutItems,
  selectedCheckoutItemsRequireShipping,
} from '../../lib/itemCheckout';
import {
  getRegistrationDonationFloor,
  getRegistrationDonationPresets,
  getRegistrationOrderShortfall,
  isRegistrationMinimumMet,
} from '../../lib/registrationCheckout';
import {
  getMissingRegistrationShippingFields,
  type RegistrationShippingField,
} from '../../lib/registration-shipping-validation';
import { DonorAddressFields } from '../../components/DonorAddressFields';
import { ADDRESS_REQUIRED_THRESHOLDS } from '../../hooks/useItemizedCheckoutController';
import { useEmbeddedCheckout } from '../hooks/useEmbeddedCheckout';
import type { CheckoutStepProps } from '../types';

/**
 * Stripe (embedded Payment Element) payment step: donation amount, optional
 * merch, shipping when an item needs it, platform-fee opt-in — then a handoff
 * to the /donate/checkout page on submit ("step 4 collects, page pays"). The
 * flow's NavigationBar drives submit via registerSubmit; outcome handling
 * after the checkout page returns lives in useCheckoutReturn at the flow level.
 */
export function StripeCheckoutStep({
  config,
  ride,
  entrant,
  onCompleted,
  onError,
  registerSubmit,
  registerSkip,
  setNavValidity,
  setNavLoading,
  setNavLabel,
  flushCheckoutState,
  returnFailure,
}: CheckoutStepProps) {
  const t = useTranslations('register');
  const tDonate = useTranslations('donate');
  const tForm = useTranslations('donate.form');
  const format = useFormatter();
  const locale = useLocale();

  // ── Store (steps 3-4 form buffer) ──
  const donationAmount = useRegistrationStore((s) => s.donationAmount);
  const customDonation = useRegistrationStore((s) => s.customDonation);
  const selectedMerch = useRegistrationStore((s) => s.selectedMerch);
  const newsletterOptIn = useRegistrationStore((s) => s.newsletterOptIn);
  const marketingOptIns = useRegistrationStore((s) => s.marketingOptIns);
  const shippingAddress = useRegistrationStore((s) => s.shippingAddress);
  const shippingAddressLine2 = useRegistrationStore((s) => s.shippingAddressLine2);
  const shippingCity = useRegistrationStore((s) => s.shippingCity);
  const shippingState = useRegistrationStore((s) => s.shippingState);
  const shippingPostcode = useRegistrationStore((s) => s.shippingPostcode);
  const shippingCountry = useRegistrationStore((s) => s.shippingCountry);
  const setDonationAmount = useRegistrationStore((s) => s.setDonationAmount);
  const setCustomDonation = useRegistrationStore((s) => s.setCustomDonation);
  const updateMerchQuantity = useRegistrationStore((s) => s.updateMerchQuantity);
  const removeMerch = useRegistrationStore((s) => s.removeMerch);
  const clearCart = useRegistrationStore((s) => s.clearCart);
  const setShipping = useRegistrationStore((s) => s.setShipping);

  const [includePlatformFee, setIncludePlatformFee] = useState(true);
  const [showShippingValidation, setShowShippingValidation] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);

  // ── Data ──
  const { data: checkoutCatalog } = trpc.publicPayment.getCheckoutCatalog.useQuery(
    { eventId: ride.eventId },
    { enabled: Boolean(ride.eventId) },
  );
  const { data: paymentConfigData } = usePaymentConfig(ride.eventId || undefined);
  const allCountriesQuery = trpc.publicLookups.allCountries.useQuery();

  const catalogItems = useMemo(() => checkoutCatalog?.items ?? [], [checkoutCatalog?.items]);
  const checkoutCurrencyCode =
    checkoutCatalog?.currencyCode ?? paymentConfigData?.paymentConfigs?.[0]?.currencyCode ?? 'USD';

  const { data: donationPresetsData } = trpc.myDashboard.goalPresets.useQuery(
    { currency: checkoutCurrencyCode, presetsUsd: config.donationPresetsUsd },
    { enabled: Boolean(entrant), staleTime: 5 * 60 * 1000 },
  );
  const { data: feeConfig } = trpc.publicDonation.getPlatformFeeCap.useQuery(
    { currencyCode: checkoutCurrencyCode },
    { staleTime: 5 * 60 * 1000 },
  );

  // ── Totals ──
  const actualDonation = customDonation ? parseFloat(customDonation) || 0 : donationAmount || 0;
  const selectedCatalogItems = useMemo(
    () => getSelectedCheckoutItems(catalogItems, selectedMerch),
    [catalogItems, selectedMerch],
  );
  const merchTotal = getCheckoutItemsSubtotal(catalogItems, selectedMerch);
  const orderTotal = actualDonation + merchTotal;
  const mandatoryDonation = ride.minimumDonation;
  const hasMandatoryDonation = mandatoryDonation > 0;
  const registrationDonationFloor = getRegistrationDonationFloor(mandatoryDonation, merchTotal);
  const registrationOrderShortfall = getRegistrationOrderShortfall(mandatoryDonation, orderTotal);
  const hasMetRegistrationMinimum = isRegistrationMinimumMet(mandatoryDonation, orderTotal);
  // itemId/quantity feed the API; name/price render on the checkout page summary
  const checkoutItemsInput = useMemo(
    () =>
      selectedCatalogItems.map(({ item, quantity }) => ({
        itemId: item.id,
        quantity,
        name: item.name,
        price: item.price,
      })),
    [selectedCatalogItems],
  );

  const platformFeeAmount = feeConfig
    ? Math.round(Math.min(orderTotal * feeConfig.feeRate, feeConfig.capAmount) * 100) / 100
    : 0;
  const payableTotal =
    includePlatformFee && platformFeeAmount > 0 ? orderTotal + platformFeeAmount : orderTotal;

  const donationPresetAmounts = useMemo(
    () =>
      getRegistrationDonationPresets(
        donationPresetsData?.map((preset) => preset.localAmount) ?? config.donationPresetsUsd,
        registrationDonationFloor,
      ),
    [donationPresetsData, config.donationPresetsUsd, registrationDonationFloor],
  );
  const currencySymbol = useMemo(
    () =>
      new Intl.NumberFormat(locale, { style: 'currency', currency: checkoutCurrencyCode })
        .formatToParts(0)
        .find((part) => part.type === 'currency')?.value ?? '$',
    [locale, checkoutCurrencyCode],
  );

  // ── Shipping ──
  const itemsRequireShipping = selectedCheckoutItemsRequireShipping(catalogItems, selectedMerch);
  const addressThreshold = ADDRESS_REQUIRED_THRESHOLDS[checkoutCurrencyCode];
  const requiresShipping =
    config.shippingRule === 'item-flag-or-threshold'
      ? itemsRequireShipping || (addressThreshold != null && orderTotal > addressThreshold)
      : itemsRequireShipping;

  const { data: shippingStateLookups } = trpc.publicLookups.statesByCountry.useQuery(
    { countryCode: shippingCountry },
    { enabled: requiresShipping && Boolean(shippingCountry) },
  );
  const missingShippingFields = useMemo(
    () =>
      getMissingRegistrationShippingFields({
        requiresShipping,
        streetAddress: shippingAddress,
        city: shippingCity,
        state: shippingState,
        postalCode: shippingPostcode,
        country: shippingCountry,
        stateRequired: (shippingStateLookups?.length ?? 0) > 0,
      }),
    [
      requiresShipping,
      shippingAddress,
      shippingCity,
      shippingState,
      shippingPostcode,
      shippingCountry,
      shippingStateLookups,
    ],
  );
  const isShippingIncomplete = missingShippingFields.length > 0;
  const shouldShowShippingValidation = showShippingValidation && isShippingIncomplete;
  const shippingFieldLabels = useMemo<Record<RegistrationShippingField, string>>(
    () => ({
      streetAddress: tDonate('streetAddress'),
      city: tForm('city'),
      state: tForm('state'),
      postalCode: tForm('postalCode'),
      country: tForm('country'),
    }),
    [tDonate, tForm],
  );

  // Prefill shipping from the saved profile once (only while the form is empty)
  const savedProfile = trpc.myProfile.getProfile.useQuery(undefined, {
    enabled: Boolean(entrant),
    retry: false,
    staleTime: Infinity,
  });
  const shippingPrefilledRef = useRef(false);
  useEffect(() => {
    if (shippingPrefilledRef.current || !savedProfile.data) return;
    shippingPrefilledRef.current = true;
    const hasShippingValues = Boolean(
      shippingAddress ||
      shippingAddressLine2 ||
      shippingCity ||
      shippingPostcode ||
      shippingCountry,
    );
    if (hasShippingValues) return;

    const profileAddress = {
      address: savedProfile.data.addressLine1 ?? '',
      addressLine2: savedProfile.data.addressLine2 ?? '',
      city: savedProfile.data.city ?? '',
      state: savedProfile.data.stateCode ?? '',
      postcode: savedProfile.data.postalCode ?? '',
      country: savedProfile.data.countryCode ?? '',
    };
    if (Object.values(profileAddress).some(Boolean)) setShipping(profileAddress);
  }, [
    savedProfile.data,
    setShipping,
    shippingAddress,
    shippingAddressLine2,
    shippingCity,
    shippingPostcode,
    shippingCountry,
  ]);

  // ── Submit paths ──
  const { startCheckout, isRedirecting } = useEmbeddedCheckout({
    eventId: ride.eventId,
    entrant,
    payableTotal,
    donationAmount: actualDonation,
    platformFee: includePlatformFee && platformFeeAmount > 0 ? platformFeeAmount : undefined,
    currencyCode: checkoutCurrencyCode,
    currencySymbol,
    recipientName: ride.eventName ?? '',
    items: checkoutItemsInput,
    donorAddress: requiresShipping
      ? {
          streetAddress: shippingAddress,
          addressLine2: shippingAddressLine2 || undefined,
          city: shippingCity,
          state: shippingState || undefined,
          postalCode: shippingPostcode,
          country: shippingCountry || undefined,
        }
      : undefined,
    donorCountryCode: ride.countryCode || undefined,
    newsletterOptIn,
    marketingOptIns,
    flushCheckoutState,
    onError,
  });

  const isLocked = isRedirecting || isCompleting;

  /** Zero-total completion — no payment, registration finishes immediately. */
  const completeWithoutPayment = useCallback(
    async (options: { skipped: boolean }) => {
      setIsCompleting(true);
      onError(null);
      try {
        await trpcVanilla.publicEntrant.completeRegistration.mutate({
          eventId: ride.eventId,
          donationAmount: 0,
          termsAccepted: true,
          newsletterOptIn,
          marketingOptIns,
        });
        trackRego('rego_completed', {
          is_waitlist: false,
          skipped_donation: options.skipped || undefined,
        });
        useRegistrationStore.getState().reset();
        onCompleted({ email: entrant?.email ?? '', donationId: null, isWaitlist: false });
      } catch (error) {
        onError(error instanceof Error ? error.message : t('registrationFailed'));
      } finally {
        setIsCompleting(false);
      }
    },
    [ride.eventId, newsletterOptIn, marketingOptIns, entrant?.email, onCompleted, onError, t],
  );

  const submit = useCallback(async () => {
    if (isLocked) return;
    if (isShippingIncomplete) {
      setShowShippingValidation(true);
      return;
    }
    if (!hasMetRegistrationMinimum) return;
    if (requiresShipping) {
      trackRego('rego_shipping_submitted', {
        country: shippingCountry,
        has_state: Boolean(shippingState),
      });
    }
    if (orderTotal > 0) {
      await startCheckout();
      return;
    }
    await completeWithoutPayment({ skipped: false });
  }, [
    isLocked,
    isShippingIncomplete,
    hasMetRegistrationMinimum,
    requiresShipping,
    shippingCountry,
    shippingState,
    orderTotal,
    startCheckout,
    completeWithoutPayment,
  ]);

  // ── Nav wiring ──
  useEffect(() => {
    registerSubmit(submit);
    return () => registerSubmit(null);
  }, [registerSubmit, submit]);

  const canSkip = config.allowSkipCheckout && !hasMandatoryDonation && orderTotal > 0;
  useEffect(() => {
    if (!canSkip) {
      registerSkip(null);
      return;
    }
    registerSkip(() => {
      clearCart();
      void completeWithoutPayment({ skipped: true });
    });
    return () => registerSkip(null);
  }, [canSkip, registerSkip, clearCart, completeWithoutPayment]);

  useEffect(() => {
    // Require entrant too: the submit path can't start checkout without it, so
    // the button must not look ready when it's missing (resume-hydration gap).
    setNavValidity(hasMetRegistrationMinimum && !isLocked && Boolean(entrant));
  }, [setNavValidity, hasMetRegistrationMinimum, isLocked, entrant]);
  useEffect(() => {
    setNavLoading(isLocked);
    return () => setNavLoading(false);
  }, [setNavLoading, isLocked]);
  useEffect(() => {
    setNavLabel(
      payableTotal > 0
        ? tDonate(
            'pay',
            { amount: payableTotal },
            { number: { currency: { style: 'currency', currency: checkoutCurrencyCode } } },
          )
        : tDonate('completeRegistration'),
    );
    return () => setNavLabel(null);
  }, [setNavLabel, payableTotal, checkoutCurrencyCode, tDonate]);

  const formatAmount = (amount: number) =>
    format.number(amount, { style: 'currency', currency: checkoutCurrencyCode });

  return (
    <div>
      {returnFailure && (
        <div
          className={`mb-6 rounded-xl border p-4 ${
            returnFailure.reason === 'failed'
              ? 'border-red-300 bg-red-50'
              : 'border-amber-300 bg-amber-50'
          }`}
          role="alert"
        >
          <div className="flex items-start gap-3">
            <AlertCircle
              className={`mt-0.5 h-5 w-5 shrink-0 ${
                returnFailure.reason === 'failed' ? 'text-red-600' : 'text-amber-600'
              }`}
            />
            <div>
              <p
                className={`font-medium ${
                  returnFailure.reason === 'failed' ? 'text-red-900' : 'text-amber-900'
                }`}
              >
                {returnFailure.reason === 'failed'
                  ? t('paymentFailedNotice')
                  : t('paymentCancelledNotice')}
              </p>
              <p
                className={`mt-1 text-sm ${
                  returnFailure.reason === 'failed' ? 'text-red-800' : 'text-amber-800'
                }`}
              >
                {t('retryPayment')}
              </p>
            </div>
          </div>
        </div>
      )}

      <h1 className="mb-2 text-3xl font-bold text-foreground md:text-4xl">
        {tDonate('supportTheCause')}
      </h1>
      <p className="mb-6 text-muted-foreground">
        {hasMandatoryDonation
          ? tDonate(
              'minimumRequired',
              { amount: mandatoryDonation, location: ride.eventName ?? '' },
              { number: { currency: { style: 'currency', currency: checkoutCurrencyCode } } },
            )
          : tDonate('subtitleNoMinimum')}
      </p>

      <div className="space-y-6">
        {/* Donation */}
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="mb-4 flex items-center gap-2">
            <Heart className="h-5 w-5 text-primary" />
            <h3 className="font-semibold text-foreground">
              {tDonate('donationLabel')}{' '}
              {hasMandatoryDonation && <span className="text-destructive">*</span>}
            </h3>
          </div>

          <div
            className={`mb-4 grid gap-2 ${
              donationPresetAmounts.length <= 2
                ? 'grid-cols-2'
                : donationPresetAmounts.length === 3
                  ? 'grid-cols-2 sm:grid-cols-3'
                  : 'grid-cols-2 sm:grid-cols-4'
            }`}
          >
            {donationPresetAmounts.map((amount) => {
              const isSelected = donationAmount === amount && !customDonation;
              return (
                <button
                  key={amount}
                  type="button"
                  disabled={isLocked}
                  onClick={() => setDonationAmount(isSelected ? null : amount)}
                  className={`h-12 rounded-lg border text-sm font-semibold transition-all ${
                    isSelected
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border bg-muted text-foreground hover:border-primary/50'
                  } ${isLocked ? 'cursor-not-allowed opacity-60' : ''}`}
                >
                  {formatAmount(amount)}
                </button>
              );
            })}
          </div>

          <div className="flex">
            <div className="flex h-12 items-center justify-center rounded-l-lg border border-r-0 border-border bg-muted px-4">
              <span className="font-medium text-muted-foreground">{currencySymbol}</span>
            </div>
            <Input
              type="number"
              placeholder={tDonate('otherAmount')}
              value={customDonation}
              onChange={(e) => setCustomDonation(e.target.value)}
              onFocus={() => setDonationAmount(null)}
              disabled={isLocked}
              className="h-12 flex-1 rounded-l-none"
              min={hasMandatoryDonation ? registrationDonationFloor : 0}
            />
          </div>

          {hasMandatoryDonation && orderTotal > 0 && registrationOrderShortfall > 0 && (
            <p className="mt-2 text-sm text-destructive">
              {tDonate(
                'minimumDonation',
                { amount: mandatoryDonation },
                { number: { currency: { style: 'currency', currency: checkoutCurrencyCode } } },
              )}
            </p>
          )}
        </div>

        {/* Merchandise */}
        {catalogItems.length > 0 && (
          <div className="rounded-xl border border-border bg-card p-5">
            <div className="mb-4 flex items-center gap-2">
              <Package className="h-5 w-5 text-primary" />
              <h3 className="font-semibold text-foreground">{tDonate('addMerchandise')}</h3>
              <span className="text-sm text-muted-foreground">({tDonate('optional')})</span>
            </div>

            <div className="space-y-3">
              {catalogItems.map((item) => {
                const isAdded = (selectedMerch[item.id] || 0) > 0;
                return (
                  <div
                    key={item.id}
                    className={`rounded-lg border p-4 transition-all ${
                      isAdded ? 'border-primary/50 bg-primary/5' : 'border-border bg-muted/40'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      {item.imageUrl && (
                        <div className="relative h-20 w-20 flex-shrink-0 overflow-hidden rounded-lg bg-muted">
                          {/* eslint-disable-next-line @next/next/no-img-element */}
                          <img
                            src={item.imageUrl}
                            alt={item.name}
                            className="absolute inset-0 h-full w-full object-cover"
                          />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <h4 className="font-medium text-foreground">{item.name}</h4>
                        <p className="mt-0.5 text-sm text-muted-foreground">{item.description}</p>
                        <div className="mt-2 flex items-center justify-between gap-3">
                          <p className="font-semibold text-primary">
                            {format.number(item.price, {
                              style: 'currency',
                              currency: item.currencyCode,
                            })}
                          </p>
                          {isAdded ? (
                            <button
                              type="button"
                              disabled={isLocked}
                              onClick={() => removeMerch(item.id)}
                              className="flex items-center gap-2 rounded-lg bg-muted px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted/70 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              <Check className="h-3.5 w-3.5 text-primary" />
                              {item.allowMultiple ? `${selectedMerch[item.id]}` : tDonate('added')}
                            </button>
                          ) : (
                            <button
                              type="button"
                              disabled={isLocked}
                              onClick={() => updateMerchQuantity(item.id, 1)}
                              className="rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {tDonate('add')}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Shipping — only when a selected item needs it */}
        {requiresShipping && (
          <div
            className={`rounded-xl border bg-card p-4 sm:p-5 ${
              shouldShowShippingValidation ? 'border-amber-400' : 'border-border'
            }`}
          >
            <div className="mb-4 flex items-center gap-2">
              <Truck className="h-5 w-5 text-primary" />
              <h3 className="font-semibold text-foreground">{tDonate('shippingAddress')}</h3>
            </div>

            {shouldShowShippingValidation && (
              <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3.5 sm:p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-amber-900">
                      {t('shippingAddressRequiredTitle')}
                    </p>
                    <p className="mt-1 text-sm text-amber-800">
                      {t('shippingAddressRequiredMessage')}
                    </p>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {missingShippingFields.map((field) => (
                    <span
                      key={field}
                      className="rounded-full border border-amber-300 bg-card px-2.5 py-1 text-xs text-amber-900"
                    >
                      {shippingFieldLabels[field]}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <DonorAddressFields
              address={{
                streetAddress: shippingAddress,
                addressLine2: shippingAddressLine2,
                city: shippingCity,
                state: shippingState,
                postalCode: shippingPostcode,
                country: shippingCountry,
              }}
              taxNumber=""
              showTaxNumber={false}
              requiredFields={shouldShowShippingValidation ? missingShippingFields : []}
              countries={allCountriesQuery.data ?? []}
              googlePlacesApiKey={paymentConfigData?.googlePlacesApiKey}
              onAddressChange={(field, value) => {
                const fieldMap = {
                  streetAddress: 'address',
                  addressLine2: 'addressLine2',
                  city: 'city',
                  state: 'state',
                  postalCode: 'postcode',
                  country: 'country',
                } as const;
                setShipping({ [fieldMap[field]]: value });
              }}
              onTaxNumberChange={() => {}}
              onCountryChange={(code) => setShipping({ country: code, state: '' })}
            />
          </div>
        )}

        {/* Order summary */}
        {orderTotal > 0 && (
          <div className="rounded-xl border border-border bg-muted/40 p-4 sm:p-5">
            <h3 className="mb-4 text-sm font-medium text-muted-foreground">
              {tDonate('orderSummary')}
            </h3>
            <div className="space-y-2 text-sm">
              {actualDonation > 0 && (
                <div className="flex items-start justify-between gap-3 text-foreground">
                  <span className="min-w-0 flex-1">{tDonate('donationLabel')}</span>
                  <span className="shrink-0 text-right">{formatAmount(actualDonation)}</span>
                </div>
              )}
              {selectedCatalogItems.map(({ item, quantity }) => (
                <div
                  key={item.id}
                  className="flex items-start justify-between gap-3 text-foreground"
                >
                  <span className="min-w-0 flex-1 break-words">
                    {tDonate('merchLineItem', { name: item.name, quantity })}
                  </span>
                  <span className="shrink-0 text-right">
                    {format.number(item.price * quantity, {
                      style: 'currency',
                      currency: item.currencyCode,
                    })}
                  </span>
                </div>
              ))}
              {includePlatformFee && platformFeeAmount > 0 && (
                <div className="flex items-start justify-between gap-3 text-foreground">
                  <span className="min-w-0 flex-1">{tForm('platformFeeLabel')}</span>
                  <span className="shrink-0 text-right">{formatAmount(platformFeeAmount)}</span>
                </div>
              )}
              <div className="my-3 h-px bg-border" />
              <div className="flex items-start justify-between gap-3 text-base font-semibold text-foreground">
                <span className="min-w-0 flex-1">{tDonate('total')}</span>
                <span className="shrink-0 text-right text-primary">
                  {formatAmount(payableTotal)}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Platform fee opt-in */}
        {feeConfig && platformFeeAmount > 0 && (
          <div className="rounded-lg border border-border bg-muted/40 p-4">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="registration-platform-fee"
                checked={includePlatformFee}
                onChange={(e) => setIncludePlatformFee(e.target.checked)}
                disabled={isLocked}
                className="mt-0.5 h-4 w-4 rounded border-border accent-primary focus:ring-primary"
              />
              <label htmlFor="registration-platform-fee" className="cursor-pointer">
                <span className="block text-sm font-medium text-foreground">
                  {tForm('platformFeeLabel')}
                </span>
                <span className="text-xs text-muted-foreground">
                  {tForm('platformFeeDescription', { amount: formatAmount(platformFeeAmount) })}
                </span>
              </label>
            </div>
          </div>
        )}

        {/* Secure-checkout note */}
        {orderTotal > 0 && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Lock className="h-4 w-4 shrink-0" />
            <span>{t('securePaymentRedirectNote')}</span>
          </div>
        )}
      </div>
    </div>
  );
}
